"use client";

import React from "react";
import { ArrowRight, ShieldCheck, Clock, Sparkles, Car, PhoneCall } from "lucide-react";
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
export default function Cta() {
  const shouldReduceMotion = useReducedMotion();
  const trustHighlights = [{
    icon: ShieldCheck,
    title: "Fixed Rates",
    text: "Guaranteed upfront flat pricing"
  }, {
    icon: Clock,
    title: "24/7 Service",
    text: "Unbroken executive availability"
  }, {
    icon: Sparkles,
    title: "Branded Luxury",
    text: "Branded premium luxury fleet"
  }, {
    icon: Car,
    title: "Regional Reach",
    text: "Illinois & multi-state coverage"
  }];
  return <section id="call-to-action" data-nav="dark" className="dark bg-background text-foreground relative overflow-hidden py-20 sm:py-28 lg:py-32">
      {/* Background panoramic image with calibrated scrim */}
      <div className="absolute inset-0 z-0 select-none">
        <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1798015/img/cta_chicago_night_expressway.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1798015/img/cta_chicago_night_expressway.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1798015/img/cta_chicago_night_expressway.png-wvc-sizes" alt="Night illumination reflecting along highway transit" loading="lazy" decoding="async" className="h-full w-full object-cover object-center opacity-25" />
        <div className="absolute inset-0 bg-gradient-to-b from-background via-background/90 to-background" />
      </div>

      {/* Decorative ambient radial gold accent glow */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 z-0 flex items-center justify-center">
        <div className="h-[380px] w-[580px] rounded-lg bg-primary/10 blur-3xl" />
      </div>

      {/* Content Container */}
      <div className="relative z-10 mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
        {/* Elevated Executive Showcase Card */}
        <motion.div initial={shouldReduceMotion ? {
        opacity: 1,
        y: 0
      } : {
        opacity: 0,
        y: 24
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: "-40px"
      }} transition={{
        duration: 0.55,
        ease: [0.16, 1, 0.3, 1]
      }} className="relative overflow-hidden rounded-xl border border-border bg-card/90 p-8 sm:p-12 lg:p-16 shadow-xl backdrop-blur-md">
          {/* Top Hairline Gold Accent Line */}
          <div aria-hidden="true" className="absolute top-0 inset-x-0 h-[1px] bg-gradient-to-r from-transparent via-primary/70 to-transparent" />

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center">
            {/* Left Narrative Column */}
            <div className="lg:col-span-7 flex flex-col items-start text-left">
              {/* Category Eyebrow */}
              <div className="inline-flex items-center gap-2 rounded-lg border border-border bg-secondary/60 px-3.5 py-1.5">
                <span className="h-1.5 w-1.5 rounded-lg bg-primary" />
                <span className="font-default text-xs tracking-wider uppercase text-primary font-semibold">
                  Executive Ground Transportation
                </span>
              </div>

              {/* Headline with Brand Accent */}
              <h2 className="mt-6 font-default text-3xl font-bold tracking-tight text-foreground sm:text-4xl lg:text-5xl leading-[1.15]">
                Reserve your ride with{" "}
                <span className="text-primary italic font-serif font-normal">
                  absolute
                </span>{" "}
                flat-rate clarity.
              </h2>

              {/* Supporting Copy */}
              <p className="mt-5 font-sans text-base sm:text-lg text-muted-foreground leading-relaxed max-w-xl">
                Experience seamless travel across Illinois and neighboring states.
                No meters, no surge spikes, and absolute transparency from pickup to drop-off.
              </p>

              {/* Conversion Buttons */}
              <div className="mt-8 flex flex-wrap items-center gap-4 w-full sm:w-auto">
                <Button asChild size="lg" className="h-12 px-7 rounded-lg bg-primary text-primary-foreground font-default font-semibold text-base shadow-md hover:bg-primary/90 transition-[background-color,box-shadow]">
                  <Link to="/OrderPage" className="inline-flex items-center gap-2">
                    <span>Book Your Ride</span>
                    <ArrowRight className="size-4" />
                  </Link>
                </Button>

                <Button asChild variant="outline" size="lg" className="h-12 px-6 rounded-lg border border-border bg-secondary/40 text-foreground font-default font-semibold text-base hover:bg-secondary hover:border-primary/40 hover:text-foreground transition-[background-color,border-color]">
                  <Link to="/DriveWithUsPage" className="inline-flex items-center gap-2">
                    <span>Drive With Us</span>
                  </Link>
                </Button>
              </div>
            </div>

            {/* Right Highlights Grid Column */}
            <div className="lg:col-span-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 gap-4 w-full">
              {trustHighlights.map((item, i) => {
              const Icon = item.icon;
              return <div key={item.text} data-index={i} className="flex items-center gap-4 rounded-lg border border-border/80 bg-background/60 p-4 transition-[border-color,background-color] hover:border-primary/40 hover:bg-background/80">
                    <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg border border-border bg-card text-primary shadow-xs">
                      <Icon className="size-5 text-primary" />
                    </div>
                    <div>
                      <div className="font-default text-xs uppercase tracking-wider text-primary font-semibold">
                        {item.title}
                      </div>
                      <div className="font-default text-sm text-foreground/90 font-medium mt-0.5">
                        {item.text}
                      </div>
                    </div>
                  </div>;
            })}
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}